// Pure logic for the "create your first offering" nudge after profile submit.
// Platform-agnostic: no React, no fetch, no app imports.
// Mirrored in bmc-marketplace-mobile/bmc-shared/src/lib/offeringNudge.ts — keep in sync.

export const OFFERING_LOOKUP_TIMEOUT_MS = 5000;
export const REVIEW_TIME_LABEL = '1–2 business days';

export const OFFERING_NUDGE_COPY = {
  modalTitle: 'Your profile is under review',
  modalIntro:
    "Thanks for submitting. We usually review profiles within 1–2 business days, and we'll email you with the outcome.",
  sectionHeading: 'Next: create your first offering',
  reasons: [
    "Students find and book you through offerings. Your profile alone isn't listed in search.",
    'You can create an offering any time, without waiting for your review.',
    'Students can see your offerings once your profile is approved.',
  ] as const,
  primaryCta: 'Create your first offering',
  secondaryCta: 'Go to dashboard',
  bannerTitle: 'Add an offering so students can find you',
  bannerText:
    "Students find and book you through offerings. Your profile alone isn't listed in search.",
} as const;

/** True once the teacher has submitted their profile at least once. */
export function hasSubmittedProfileAtLeastOnce(
  draftStatus: string | null | undefined,
  hasActiveVersion: boolean,
): boolean {
  return draftStatus === 'PENDING_REVIEW' || draftStatus === 'REJECTED' || hasActiveVersion;
}

/** Number of offerings in a response, or null when the shape is unrecognised. */
export function countOfferings(response: unknown): number | null {
  if (Array.isArray(response)) return response.length;
  if (typeof response === 'object' && response !== null && 'offerings' in response) {
    const offerings = (response as { offerings: unknown }).offerings; // narrowed by 'in' check above
    return Array.isArray(offerings) ? offerings.length : null;
  }
  return null;
}

export type PostSubmitNudge = 'SHOW_NUDGE' | 'SKIP_NUDGE';

/** Never rejects. SKIP_NUDGE only when the lookup confirms at least one offering. */
export async function resolvePostSubmitNudge(
  lookup: () => Promise<unknown>,
  timeoutMs: number = OFFERING_LOOKUP_TIMEOUT_MS,
): Promise<PostSubmitNudge> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), timeoutMs);
    });
    const result = await Promise.race([Promise.resolve().then(lookup), timeout]);
    const count = countOfferings(result);
    return count !== null && count > 0 ? 'SKIP_NUDGE' : 'SHOW_NUDGE';
  } catch {
    return 'SHOW_NUDGE';
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export function shouldShowOfferingBanner(i: {
  profileLoading: boolean;
  draftStatus: string | null | undefined;
  hasActiveVersion: boolean;
  offeringsStatus: 'pending' | 'error' | 'success';
  offeringsData: unknown;
}): boolean {
  return (
    !i.profileLoading &&
    i.offeringsStatus === 'success' &&
    countOfferings(i.offeringsData) === 0 &&
    hasSubmittedProfileAtLeastOnce(i.draftStatus, i.hasActiveVersion)
  );
}
